import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Modal, ModalBody } from "@/components/bootstrap/TemporaryReactstrapCompatibilityLayer";
import AuthContext from "../../../contexts/auth/AuthContext";
import AutoComplete from "../../container-management/AutoComplete";
import GoogleLocationMap from "../../container-management/GoogleLocationMap";
import Button from "../../layout/Button";
import StatusBarButton from "../../layout/StatusBarButton";
import ProcessContext from "../../../contexts/process/ProcessContext";
import ContainersApiService from "../../../services/apiServices/ContainersApiService";
import HarvestingProceduresApiService from "../../../services/apiServices/HarvestingProceduresApiService";
import { SINGLE_HARVEST_ROOTED, harvestingProcedureTypes } from "../../../constants/harvestingProcedures";
import { separateOnCapitalLetters } from "../../../utility/PascalCaseSeperator";
import FarmhandInstallationsApiService from "../../../services/apiServices/FarmhandInstallationsApiService";
import UIContext from "../../../contexts/ui/UIContext";
import { HELP_DESK_LINK } from "../../../constants/helpDesk";
import ConfirmDeleteModal from '../seedTypes/ConfirmDeleteModal';
import { geocodeLatLng } from "../../../services/googleMapFactory";

const Create = () => {
    const navigate = useNavigate();
    const { currentTenant, currentContainer, accessibleContainers, setAccessibleContainers, setCurrentContainer } = useContext(AuthContext);
    const { notifySuccess, notifyWarning, notifyError } = useContext(ProcessContext);
    const { setInfo } = useContext(UIContext);

    // Initial State Template
    const initialContainerModelState = {
        name: { value: '', touched: false, isValid: false, validate: (text) => text?.length >= 3 },
        description: { value: '', touched: false, isValid: true, validate: () => true },
        farmhandId: { value: '', touched: false, isValid: true, validate: () => true },
        storageAdviceRoot: { value: '', touched: false, isValid: true, validate: () => true },
        storageAdviceCut: { value: '', touched: false, isValid: true, validate: () => true },
        location: {
            value: { latitude: null, longitude: null, street: '', number: '', postalCode: '', city: '', country: '' }
        }
    };

    const [modelState, setModelState] = useState(initialContainerModelState);
    const [localLoading, setLocalLoading] = useState(false);
    const [locationString, setLocationString] = useState('');
    const [location, setLocation] = useState({ lat: 50.9396911, lng: 6.8551075 });
    const [openLocation, setOpenLocation] = useState(false);
    const [selectedHarvestProcedures, setSelectedHarvestProcedures] = useState([]);
    const [farmhandInstallation, setFarmhandInstallation] = useState(null);
    const [currentSelectValue, setCurrentSelectValue] = useState(harvestingProcedureTypes[0]);

    useEffect(() => {
        setInfo('Create Container');
    }, []);

    useEffect(() => {
        if (accessibleContainers?.length === 1 && !currentContainer) {
            setCurrentContainer(accessibleContainers[0]);
            navigate('/management/containers/list');
        }
    }, [accessibleContainers, currentContainer, navigate, setCurrentContainer]);

    const handleAbortNavigaton = () => {
        accessibleContainers?.length === 0 ? navigate('/') : navigate('/management/containers/list');
    };

    const handleLocationUpdate = useCallback((place) => {
        const getComp = (type) => place.address_components.find(c => c.types.includes(type))?.long_name || '';

        const newLocationData = {
            number: getComp('street_number'),
            street: getComp('route'),
            postalCode: getComp('postal_code'),
            city: getComp('locality') || getComp('postal_town'),
            country: getComp('country'),
            latitude: place.geometry?.location.lat() || place.lat,
            longitude: place.geometry?.location.lng() || place.lng,
        };

        setModelState(prev => ({
            ...prev,
            location: { ...prev.location, value: newLocationData }
        }));
        setLocationString(place.formatted_address || `${newLocationData.street} ${newLocationData.number}`);
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        if (!modelState[name]) return;

        setModelState(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                value,
                touched: true,
                isValid: prev[name].validate(value)
            }
        }));
    };

    const validateFarmhandInstallation = async () => {
        const val = modelState.farmhandId.value;
        if (!val) return;

        try {
            const res = await FarmhandInstallationsApiService.getFarmHandInstallationId(val);
            const usageRes = await FarmhandInstallationsApiService.getContainerForFarmHandInstallationId(res.data.data.id);

            if (usageRes.data.data.id != null) {
                notifyWarning("The farmhand installation is already in use.");
                setFarmhandInstallation(null);
            } else {
                setFarmhandInstallation(res.data.data);
            }
        } catch (error) {
            if (error.response?.status === 404) notifyWarning("Farmhand installation does not exist.");
            else notifyError("Error checking farmhand ID.");
            setFarmhandInstallation(null);
        }
    };

    const handleAddHarvestProcedure = () => {
        if (!selectedHarvestProcedures.includes(currentSelectValue)) {
            setSelectedHarvestProcedures([...selectedHarvestProcedures, currentSelectValue]);
        }
    };

    const handleRemoveHarvestProcedure = (proc) => {
        setSelectedHarvestProcedures(prev => prev.filter(p => p !== proc));
    };

    const onConfirm = async () => {
        setLocalLoading(true);

        const isFirstContainer = !accessibleContainers || accessibleContainers.length === 0;

        const containerData = {
            name: modelState.name.value,
            description: modelState.description.value,
            ...modelState.location.value
        };

        try {
            const res = await ContainersApiService.createContainer(currentTenant.id, containerData);
            const newId = res.data.data.id;

            const promises = [];
            selectedHarvestProcedures.forEach(type => {
                promises.push(HarvestingProceduresApiService.createHarvestingProcedure(currentTenant.id, newId, {
                    type,
                    storageAdvice: type === SINGLE_HARVEST_ROOTED ? modelState.storageAdviceRoot.value : modelState.storageAdviceCut.value
                }));
            });

            if (farmhandInstallation) {
                promises.push(FarmhandInstallationsApiService.assignFarmHandInstallationToContainer(currentTenant.id, newId, { farmHandInstallationId: farmhandInstallation.id }));
            }

            await Promise.allSettled(promises);

            await setAccessibleContainers(currentTenant);
            notifySuccess("Successfully created container.");

            if (!isFirstContainer) {
                navigate('/management/containers/list');
            }

        } catch (error) {
            handleApiError(error);
        } finally {
            setLocalLoading(false);
        }
    };

    const handleApiError = (error) => {
        console.error(error);
        const apiErrorCode = error.response?.data?.code;

        if (apiErrorCode === 400) {
            notifyWarning("The container name is missing. Please provide a name for the container and try again.");
        } else if (apiErrorCode === 409) {
            notifyWarning("The container name already exists. Please choose a different name and try again.");
        } else if (apiErrorCode === 403 || apiErrorCode === 404) {
            notifyError(<>We have encountered an issue... contact support {HELP_DESK_LINK}.</>);
        } else {
            notifyError(<>We have encountered an issue on our side... {HELP_DESK_LINK}.</>);
        }
    };

    const handlePlacePicked = useCallback((place) => {
        if (!place?.geometry?.location) return;

        handleLocationUpdate(place);
        setLocation({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
    }, [handleLocationUpdate]);

    const handleMapLocationClick = useCallback(async (latlng) => {
        try {
            const response = await geocodeLatLng(latlng);
            if (response.results[0]) {
                handleLocationUpdate(response.results[0]);
                setLocation(latlng);
            }
        } catch (error) {
            console.error("Map error.", error);
            notifyWarning("Map error. Please try again.");
        }
    }, [handleLocationUpdate, notifyWarning]);

    return (
        <>
            <div className='create-container-container'>
                <div className='create-container-card-container'>
                    <div className='create-container-card2'>
                        <div className='create-container-header general-information'>General Information</div>

                        {/* Name Input */}
                        <div className='name-input-container'>
                            <div className='name-input-label'><i className='fa-solid fa-signature'></i> Name</div>
                            <input
                                type='text'
                                name='name'
                                value={modelState.name.value}
                                onChange={onChange}
                                className={`name-input ${(!modelState.name.isValid && modelState.name.touched) ? 'red-border' : ''}`}
                            />
                            {(!modelState.name.isValid && modelState.name.touched) &&
                                <p className='name-input-error'>Name should have three characters or more.</p>
                            }
                        </div>

                        {/* Location Trigger */}
                        <div className='select-location-container'>
                            <div className='select-location-label'><i className='fa-solid fa-location-dot'></i> Location</div>
                            <div className='location-output'>
                                <p className='location-output-text'>{locationString || "Please specify the exact location of the container."}</p>
                            </div>
                            <button className='select-location-button' onClick={() => setOpenLocation(true)}>
                                <i className='fas fa-pen'></i>
                            </button>
                        </div>

                        <div className='create-container-header miscellaneous'>Miscellaneous</div>

                        {/* Farmhand ID */}
                        <div className='farmhand-id-input-container'>
                            <div className='farmhand-id-label'><i className='fa-solid fa-tractor'></i> Farmhand ID</div>
                            <input
                                type='text'
                                name='farmhandId'
                                value={modelState.farmhandId.value}
                                onChange={onChange}
                                onBlur={validateFarmhandInstallation}
                                className='farmhand-id-input'
                            />
                        </div>

                        {/* Storage Advice */}
                        {selectedHarvestProcedures.some(hp => hp.includes('Root')) && (
                            <div className='storage-advice-root-input-container'>
                                <div className='storage-advice-root-label'><i className="fas fa-archive"></i> Storage Advice (Root)</div>
                                <textarea name='storageAdviceRoot' value={modelState.storageAdviceRoot.value} onChange={onChange} className='storage-advice-root-input' />
                            </div>
                        )}

                        {selectedHarvestProcedures.some(hp => hp.includes('Cut')) && (
                            <div className='storage-advice-cut-input-container'>
                                <div className='storage-advice-cut-label'><i className="fas fa-archive"></i> Storage Advice (Cut)</div>
                                <textarea name='storageAdviceCut' value={modelState.storageAdviceCut.value} onChange={onChange} className='storage-advice-cut-input' />
                            </div>
                        )}

                        {/* Description */}
                        <div className='description-textarea-container'>
                            <div className='description-label'><i className='fa-regular fa-comment-dots'></i> Description</div>
                            <textarea rows={5} name='description' value={modelState.description.value} onChange={onChange} className='description-input' />
                        </div>

                        {/* Harvesting Procedures */}
                        <div className='harvesting-procedures-container'>
                            <div className='harvesting-procedures-label'><i className='fa-solid fa-wheat-awn'></i> Harvesting Procedures</div>
                            <select
                                className='select-harvesting-procedures'
                                value={currentSelectValue}
                                onChange={(e) => setCurrentSelectValue(e.target.value)}
                            >
                                {harvestingProcedureTypes.map((type, i) => (
                                    <option key={i} value={type}>{separateOnCapitalLetters(type)}</option>
                                ))}
                            </select>
                            <button className='select-harvesting-procedures-button' onClick={handleAddHarvestProcedure}>
                                <i className='fa-solid fa-plus'></i>
                            </button>
                            <div className='select-harvesting-procedures-content'>
                                {selectedHarvestProcedures.map((type, i) => (
                                    <div key={i} className='select-harvesting-procedures-content-option'>
                                        <i className="fa-solid fa-x remove-button" onClick={() => handleRemoveHarvestProcedure(type)}></i>
                                        <span>{separateOnCapitalLetters(type)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Buttons */}
                <StatusBarButton statusSlot={3} label='Abort' clickHandler={handleAbortNavigaton} icon='fas fa-ban' type='inline' />
                <StatusBarButton
                    label='Confirm'
                    statusSlot={5}
                    icon='fas fa-check'
                    type='inline'
                    reversed={true}
                    clickHandler={onConfirm}
                    disabled={!modelState.name.isValid || localLoading}
                />
            </div>

            {/* Modals */}
            <Modal size='xl' centered isOpen={openLocation} toggle={() => setOpenLocation(false)} className='modal-config'>
                <ModalBody className='location-card-body'>
                    <AutoComplete
                        locationPicked={handlePlacePicked}
                        currentLocation={locationString}
                        closeLocation={() => setOpenLocation(false)}
                    />
                    <div className='location-map-container'>
                        {location && (
                            <GoogleLocationMap
                                center={location}
                                onLocationClick={handleMapLocationClick}
                                onError={() => notifyWarning("Map error. Please try again.")}
                            />
                        )}
                    </div>
                    {/* BUTTON MOVED INSIDE MODAL BODY TO AVOID OVERLAY BLOCKING */}
                    <div className="text-center mt-3">
                        <Button className='location-card-btn' label='Done' clickHandler={() => setOpenLocation(false)} />
                    </div>
                </ModalBody>
            </Modal>

            <ConfirmDeleteModal
                show={localLoading}
                hideActions
                header="Container creation in progress..."
                message={
                    <>
                        <p>Creating the container may take up to several minutes. Please don&apos;t navigate away.</p>
                        <i className='fas fa-cog fa-spin' />
                    </>
                }
            />
        </>
    );
};

export default Create;
